/**
 * Contact ladders (WP-X1, docs/CONTACTS.md): standing with the eight people who matter in Kessmouth — Mags, Des
 * Halloran, Sid, Lenny, Wilf, Pike, Dot and Rita. Each is a ladder of five rungs climbed by doing their kind of
 * work; every rung opens ACCESS (a page of the book), a PRICE BREAK or a small PAY BONUS, and the middle and top
 * rungs are titles. Never power: no rung changes health, damage, speed, carrying limits or any validation limit
 * (`PERK_KINDS` is the whole list of what a rung may do, and contacts.test.ts asserts it).
 *
 *  - Offline the points are kept in the save (`SaveData.contacts`, src/contacts/contactsSystem.ts).
 *  - Online they are derived by the server from the ledger (server/economy/contacts.ts): every point is a row
 *    of money that already moved for that character, so a replayed request can never count twice, an alt or a
 *    gang treasury never lends standing, and existing characters start with what they have already done.
 *
 * Repeatable work is capped per track per day (`CONTACT_DAILY_CAP`, like gang reputation); one-off firsts (a
 * story job done, a matchbook found) are not. Pure: no DOM, no Node APIs.
 */

export const CONTACT_TRACKS = ['mags', 'des', 'sid', 'lenny', 'wilf', 'pike', 'dot', 'rita'] as const;
export type ContactTrack = (typeof CONTACT_TRACKS)[number];
export type ContactMode = 'offline' | 'online';

/** Points needed for each rung (index = rung; rung 0 is a stranger). */
export const RUNG_POINTS: readonly number[] = [0, 15, 45, 100, 180, 300];
export const MAX_RUNG = RUNG_POINTS.length - 1;
/** Points a track can gain from repeatable work in one day (UTC day). */
export const CONTACT_DAILY_CAP = 60;
export const DAY_MS = 86_400_000;

/**
 * Everything a rung may do. `discount` and `bonus` are fractions of a price / a payout; `access` opens a part
 * of the book. Nothing else exists, on purpose: this list is the "options, never power" invariant.
 */
export const PERK_KINDS = {
  /** Desk sergeant's price (offline). */
  sergeant: 'discount',
  /** A big buyer's introduction fee (online). */
  'market-intro': 'discount',
  /** The out-of-town and big buyers show in the book (online, progressive disclosure). */
  'access-markets': 'access',
  /** Halloran's workshop prices (offline). */
  'halloran-work': 'discount',
  /** Halloran's export orders pay more (offline). */
  'export-pay': 'bonus',
  /** A car off Halloran's forecourt (online). */
  car: 'discount',
  /** Body work at Halloran's (online). */
  repair: 'discount',
  /** Sid's prices: weapons and rounds. */
  sid: 'discount',
  /** Lenny's street jobs pay more (offline). */
  'street-pay': 'bonus',
  /** Courier and recovery jobs pay more (online). */
  'job-pay': 'bonus',
  /** The Turf pages for a character without a gang (online, progressive disclosure). */
  'access-turf': 'access',
  /** The Anchor back room's gear: holdall, scanner, suit (online). */
  gear: 'discount',
  /** The holdall from Wilf, free (online; it is still one per character). */
  'holdall-free': 'discount',
  /** Pike's respray. */
  respray: 'discount',
  /** Pike's card: wheels, finish, neon, ride and the rest. */
  mods: 'discount',
  /** Pike's "keep this one". */
  keep: 'discount',
  /** Minicab shift takings (offline). */
  'cab-pay': 'bonus',
  /** The minicab fare home or back to a job (offline). */
  minicab: 'discount',
  /** Rita's pot (offline). */
  'race-pay': 'bonus',
} as const;
export type PerkKey = keyof typeof PERK_KINDS;

/** The largest fraction any discount / bonus may be (the free holdall and the free minicab are whole items). */
export const MAX_BONUS = 0.15;
export const MAX_DISCOUNT = 0.5;
export const WHOLE_ITEM_PERKS: readonly PerkKey[] = ['holdall-free', 'minicab'];

export interface Perk {
  key: PerkKey;
  /** Fraction (0.1 = 10 %). */
  value: number;
  /** Only in this mode (absent = both). */
  mode?: ContactMode;
  /** Plain words for the ladder ("10 % off Sid's prices"). */
  text: string;
}

export interface RungDef {
  name: string;
  /** The rung's name is a title the book shows beside yours. */
  title?: boolean;
  perks: readonly Perk[];
  /** What the contact says when you reach it (spoken: slang is fine here). */
  line: string;
}

export interface TrackDef {
  id: ContactTrack;
  /** As the book names them. */
  name: string;
  short: string;
  /** The book's CONTACTS id (src/book/contacts.ts) whose page carries the ladder. */
  page: string;
  modes: readonly ContactMode[];
  /** Rungs 1..5. */
  rungs: readonly RungDef[];
}

const off = (key: PerkKey, value: number, text: string): Perk => ({ key, value, mode: 'offline', text });
const on = (key: PerkKey, value: number, text: string): Perk => ({ key, value, mode: 'online', text });
const both = (key: PerkKey, value: number, text: string): Perk => ({ key, value, text });

export const TRACKS: Readonly<Record<ContactTrack, TrackDef>> = {
  mags: {
    id: 'mags',
    name: 'Mags Delaney',
    short: 'Mags',
    page: 'mags',
    modes: ['offline', 'online'],
    rungs: [
      {
        name: 'Known at the Anchor',
        perks: [off('sergeant', 0.1, '10 % off the desk sergeant — Mags drinks with him'), on('access-markets', 1, 'The out-of-town and big buyers open in your book')],
        line: 'You’ll do. Tell the desk at the nick you’re one of mine, and I’ll put a word in with the buyers who matter.',
      },
      {
        name: 'Trusted',
        perks: [off('sergeant', 0.2, '20 % off the desk sergeant'), on('market-intro', 0.25, '25 % off a big buyer’s introduction fee')],
        line: 'You’ve not let me down yet. People are starting to notice that.',
      },
      { name: 'One of Mags’', title: true, perks: [], line: 'From tonight you’re one of mine, and the whole borough knows it. Don’t make me sorry.' },
      {
        name: 'Her right hand',
        perks: [off('sergeant', 0.3, '30 % off the desk sergeant'), on('market-intro', 0.5, 'Half off a big buyer’s introduction fee')],
        line: 'I don’t say this often. You’re the one I ring first.',
      },
      { name: 'Family', title: true, perks: [], line: 'Family, love. Nobody touches family.' },
    ],
  },
  des: {
    id: 'des',
    name: 'Des Halloran',
    short: 'Halloran',
    page: 'halloran',
    modes: ['offline', 'online'],
    rungs: [
      { name: 'A face on the forecourt', perks: [off('halloran-work', 0.05, '5 % off Halloran’s workshop'), on('repair', 0.25, '25 % off body work at Halloran’s')], line: 'Seen you about. You bring ’em in clean, I’ll look after you.' },
      { name: 'A regular', perks: [off('export-pay', 0.05, 'Export orders pay 5 % more'), on('car', 0.05, '5 % off a car on the forecourt')], line: 'Regular as clockwork, you. The boat likes your motors.' },
      { name: 'Des’s mate', title: true, perks: [off('halloran-work', 0.1, '10 % off Halloran’s workshop'), on('repair', 0.5, 'Half off body work at Halloran’s')], line: 'Mate’s rates from now on. Don’t tell the others.' },
      { name: 'Trade terms', perks: [off('export-pay', 0.1, 'Export orders pay 10 % more'), on('car', 0.1, '10 % off a car on the forecourt')], line: 'Trade terms, same as the dealers get. Earned every penny.' },
      { name: 'Like a son', title: true, perks: [off('halloran-work', 0.15, '15 % off Halloran’s workshop'), on('car', 0.15, '15 % off a car on the forecourt')], line: 'My old man had this yard before me. If I had a lad, I’d want him driving like you.' },
    ],
  },
  sid: {
    id: 'sid',
    name: 'Sid Marlowe',
    short: 'Sid',
    page: 'sid',
    modes: ['offline', 'online'],
    rungs: [
      { name: 'Not a copper', perks: [both('sid', 0.05, '5 % off Sid’s prices')], line: 'All right, you’re not Old Bill. Nobody from the nick buys that many rounds.' },
      { name: 'A customer', perks: [both('sid', 0.1, '10 % off Sid’s prices')], line: 'A proper customer. I keep a little something off for proper customers.' },
      { name: 'Sid’s regular', title: true, perks: [], line: 'Kettle’s on whenever you’re passing. Regulars get tea.' },
      { name: 'Under the counter', perks: [both('sid', 0.15, '15 % off Sid’s prices')], line: 'Under the counter prices, son. Same as I give my brother-in-law, and I don’t like him.' },
      { name: 'Old friend', title: true, perks: [], line: 'Twenty years in this yard and I can count my friends on one hand. You’re on it.' },
    ],
  },
  lenny: {
    id: 'lenny',
    name: 'Lenny the Ear',
    short: 'Lenny',
    page: 'lenny',
    modes: ['offline', 'online'],
    rungs: [
      { name: 'On the list', perks: [off('street-pay', 0.05, 'Street jobs pay 5 % more'), on('job-pay', 0.05, 'Courier and recovery jobs pay 5 % more')], line: 'You’re on my list now. The good list. I get a better rate for names on the good list.' },
      { name: 'Reliable', perks: [off('street-pay', 0.1, 'Street jobs pay 10 % more'), on('access-turf', 1, 'Who runs which district: the Turf pages open in your book')], line: 'Reliable, that’s what I tell people. And I hear what they say back — about whose streets are whose.' },
      { name: 'Lenny’s ear', title: true, perks: [on('job-pay', 0.1, 'Courier and recovery jobs pay 10 % more')], line: 'You hear what I hear now. Don’t go repeating it.' },
      { name: 'First call', perks: [off('street-pay', 0.15, 'Street jobs pay 15 % more'), on('job-pay', 0.15, 'Courier and recovery jobs pay 15 % more')], line: 'When the good work comes in, I ring you first. Then I ring the rest.' },
      { name: 'Word of Lenny', title: true, perks: [], line: 'Half this borough does what I say because they think you’re behind me. Leave it that way.' },
    ],
  },
  wilf: {
    id: 'wilf',
    name: 'Wilf Tuckett',
    short: 'Wilf',
    page: 'wilf',
    modes: ['online'],
    rungs: [
      { name: 'Not a grass', perks: [on('gear', 0.1, '10 % off the gear in the Anchor’s back room')], line: 'You’ve not grassed. Yet. I’ll have a word at the Anchor about the gear.' },
      { name: 'Sound', perks: [on('holdall-free', 1, 'A holdall, free: Wilf leaves one in the Anchor’s back room')], line: 'You’re sound. There’s a holdall behind the bar at the Anchor with your name on it. Use it.' },
      { name: 'Wilf’s friend', title: true, perks: [], line: 'I don’t have friends, as a rule. Rules change.' },
      { name: 'Trusted with the arches', perks: [on('gear', 0.2, '20 % off the gear in the Anchor’s back room')], line: 'You know where the arches’ key is kept now. That’s more than my wife does.' },
      { name: 'Partner in the arches', title: true, perks: [], line: 'Half the borough’s goods go through these arches and half of them go through you. Partners, then.' },
    ],
  },
  pike: {
    id: 'pike',
    name: 'Pike’s Respray',
    short: 'Pike',
    page: 'pike',
    modes: ['offline', 'online'],
    rungs: [
      { name: 'Paying customer', perks: [both('respray', 0.1, '10 % off a respray')], line: 'Cash on the nail. I like that in a customer.' },
      { name: 'Known car', perks: [both('mods', 0.05, '5 % off Pike’s card')], line: 'I know your motor by the sound of it now. That’s worth something.' },
      { name: 'Pike’s regular', title: true, perks: [both('keep', 0.25, '25 % off “keep this one”')], line: 'Regulars get the good thinners. And the good price on the papers.' },
      { name: 'Trade price', perks: [both('respray', 0.2, '20 % off a respray'), both('mods', 0.1, '10 % off Pike’s card')], line: 'Trade price. Don’t bring your mates, they don’t get it.' },
      { name: 'Pike’s pride', title: true, perks: [both('keep', 0.5, 'Half off “keep this one”')], line: 'Half the motors on this manor are my paint. The best of them are yours.' },
    ],
  },
  dot: {
    id: 'dot',
    name: 'Dot Hennessy',
    short: 'Dot',
    page: 'dot',
    modes: ['offline'],
    rungs: [
      { name: 'On the rota', perks: [off('cab-pay', 0.05, 'Minicab shifts pay 5 % more')], line: 'You’re on the rota, love. I’ll see you right on the good fares.' },
      { name: 'Good driver', perks: [off('minicab', 1 / 3, 'The minicab is £5 cheaper')], line: 'The lads say you’re a good driver. Have a cab on us when you need one — well, most of one.' },
      { name: 'Dot’s favourite', title: true, perks: [], line: 'Don’t tell the others you’re my favourite. They all think they are.' },
      { name: 'Night shift king', perks: [off('cab-pay', 0.1, 'Minicab shifts pay 10 % more'), off('minicab', 1, 'The minicab is free')], line: 'Night shift’s yours. And your cab’s on the house, any time.' },
      { name: 'Kestrel legend', title: true, perks: [], line: 'They’ll talk about your shifts at Kestrel Cars long after I’ve hung up the phone.' },
    ],
  },
  rita: {
    id: 'rita',
    name: 'Rita Vance',
    short: 'Rita',
    page: 'rita',
    modes: ['offline'],
    rungs: [
      { name: 'On the grid', perks: [off('race-pay', 0.05, 'Rita adds 5 % to the pot you win')], line: 'You turned up and you finished. Half the quay can’t say that.' },
      { name: 'A real driver', perks: [off('race-pay', 0.1, 'Rita adds 10 % to the pot you win')], line: 'A real driver. I’ll sweeten the pot when you win, it brings the crowd.' },
      { name: 'Rita’s rival', title: true, perks: [], line: 'I don’t lose sleep over many drivers. I lose it over you.' },
      { name: 'Quay royalty', perks: [off('race-pay', 0.15, 'Rita adds 15 % to the pot you win')], line: 'The quay goes quiet when you roll up to the line. Enjoy it.' },
      { name: 'Fastest on the quay', title: true, perks: [], line: 'Fastest on the quay. I said it out loud, so it must be true.' },
    ],
  },
};

// ----------------------------------------------------------------------------- where points come from

export const SOURCE_KEYS = [
  // offline
  'story',
  'story-replay',
  'export',
  'garage-work',
  'street',
  'matchbook',
  'race',
  'race-win',
  'cab',
  'fence-buy',
  'respray',
  'spray-bay',
  // online
  'job',
  'fence-sale',
  'supply-buy',
  'arms-buy',
  'freight',
  'firm-job',
  'big-sale',
  'wholesale',
  'car-bought',
  'rare-car',
  'repair',
  'mod',
  'errand',
] as const;
export type SourceKey = (typeof SOURCE_KEYS)[number];

export interface SourceDef {
  track: ContactTrack;
  points: number;
  /** Counts against the track's daily cap (false: a one-off first, bounded by the game itself). */
  capped: boolean;
  modes: readonly ContactMode[];
  /** "a street job" — how the ladder says it. */
  label: string;
  /** One and many, for "2 more street jobs" (the NEXT tracker's horizon). */
  noun: readonly [string, string];
}

const src = (track: ContactTrack, points: number, label: string, noun: readonly [string, string], modes: readonly ContactMode[], capped = true): SourceDef => ({ track, points, capped, modes, label, noun });
const OFF: readonly ContactMode[] = ['offline'];
const ON: readonly ContactMode[] = ['online'];

export const SOURCES: Readonly<Record<SourceKey, SourceDef>> = {
  story: src('mags', 25, 'a story job done for the first time', ['story job', 'story jobs'], OFF, false),
  'story-replay': src('mags', 6, 'a story job played again', ['replay', 'replays'], OFF),
  export: src('des', 8, 'an export order delivered', ['export order', 'export orders'], OFF),
  'garage-work': src('des', 4, 'a job done at Halloran’s workshop', ['job at the workshop', 'jobs at the workshop'], OFF),
  street: src('lenny', 8, 'a street job off the Coalgate box', ['street job', 'street jobs'], OFF),
  matchbook: src('lenny', 4, 'a matchbook found', ['matchbook', 'matchbooks'], OFF, false),
  race: src('rita', 4, 'a race finished', ['race', 'races'], OFF),
  'race-win': src('rita', 10, 'a race won', ['race won', 'races won'], OFF),
  cab: src('dot', 8, 'a minicab shift driven', ['minicab shift', 'minicab shifts'], OFF),
  'fence-buy': src('sid', 5, 'anything bought from Sid', ['buy from Sid', 'buys from Sid'], OFF),
  respray: src('pike', 4, 'a respray', ['respray', 'resprays'], ['offline', 'online']),
  'spray-bay': src('pike', 6, 'work paid for in Pike’s spray bay', ['visit to the spray bay', 'visits to the spray bay'], OFF),
  job: src('lenny', 3, 'a courier or recovery job paid', ['job', 'jobs'], ON),
  'fence-sale': src('wilf', 2, 'goods sold to a fence or on the street (£100 or more)', ['sale', 'sales'], ON),
  'supply-buy': src('wilf', 1, 'goods bought from a supplier', ['buy from a supplier', 'buys from a supplier'], ON),
  'arms-buy': src('sid', 4, 'anything bought from Sid', ['buy from Sid', 'buys from Sid'], ON),
  freight: src('mags', 10, 'a Night Freight run that paid you', ['Night Freight run', 'Night Freight runs'], ON),
  'firm-job': src('mags', 12, 'a firm’s job pulled off (the Savings Bank)', ['firm’s job', 'firm’s jobs'], ON),
  'big-sale': src('mags', 4, 'a sale to a big buyer', ['big-buyer sale', 'big-buyer sales'], ON),
  wholesale: src('mags', 3, 'a cut of the gang’s wholesale takings', ['wholesale cut', 'wholesale cuts'], ON),
  'car-bought': src('des', 20, 'a car bought off Halloran’s forecourt', ['car bought', 'cars bought'], ON),
  'rare-car': src('des', 10, 'a rare motor handed in', ['rare motor', 'rare motors'], ON),
  repair: src('des', 2, 'body work at Halloran’s', ['repair', 'repairs'], ON),
  mod: src('pike', 6, 'work on Pike’s card (or “keep this one”)', ['job in the spray bay', 'jobs in the spray bay'], ON),
  // Lenny's list (WP-X8, shared/lennyList.ts): three a day at most, under his daily cap like his jobs.
  errand: src('lenny', 8, 'an errand off Lenny’s list (three a day)', ['errand', 'errands'], ON),
};

/** Sources the NEXT tracker never counts in (Lenny's list is three a day: "5 more errands" would be a chore). */
const NOT_A_HORIZON: readonly SourceKey[] = ['errand'];

// ----------------------------------------------------------------------------- standing

/** Points per track (absent = 0). */
export type Standing = Partial<Record<ContactTrack, number>>;

/** Offline state: points, and today's capped points per track. */
export interface ContactsState {
  pts: Standing;
  /** UTC day number `today` counts for. */
  day: number;
  today: Standing;
}

export const emptyContacts = (): ContactsState => ({ pts: {}, day: 0, today: {} });
export const dayOf = (ms: number): number => Math.floor(ms / DAY_MS);

export function isTrack(v: unknown): v is ContactTrack {
  return typeof v === 'string' && (CONTACT_TRACKS as readonly string[]).includes(v);
}

/** The rung `points` reaches (0..MAX_RUNG). */
export function rungOf(points: number): number {
  let r = 0;
  for (let i = 1; i < RUNG_POINTS.length; i++) if (points >= RUNG_POINTS[i]!) r = i;
  return r;
}

export interface Progress {
  rung: number;
  points: number;
  /** Points for the next rung (null at the top). */
  next: number | null;
  /** 0..1 of the way from this rung to the next (1 at the top). */
  frac: number;
}

export function progressOf(points: number): Progress {
  const p = Math.max(0, Math.floor(points));
  const rung = rungOf(p);
  const next = rung < MAX_RUNG ? RUNG_POINTS[rung + 1]! : null;
  const lo = RUNG_POINTS[rung]!;
  return { rung, points: p, next, frac: next === null ? 1 : (p - lo) / (next - lo) };
}

export interface AwardResult {
  state: ContactsState;
  track: ContactTrack;
  gained: number;
  /** The rung before and after (equal when nothing changed). */
  from: number;
  to: number;
}

/**
 * Offline: `times` of `source` happened on day `day`. Capped sources fill the track's daily allowance; one-offs
 * always count. Pure (returns a new state).
 */
export function award(state: ContactsState, source: SourceKey, day: number, times = 1): AwardResult {
  const s = SOURCES[source];
  const pts = { ...state.pts };
  const today = state.day === day ? { ...state.today } : {};
  const want = Math.max(0, Math.floor(s.points * times));
  const used = today[s.track] ?? 0;
  const gained = s.capped ? Math.max(0, Math.min(want, CONTACT_DAILY_CAP - used)) : want;
  const before = pts[s.track] ?? 0;
  pts[s.track] = before + gained;
  if (s.capped) today[s.track] = used + gained;
  return { state: { pts, day, today }, track: s.track, gained, from: rungOf(before), to: rungOf(before + gained) };
}

/**
 * Online: standing from counts of sources per UTC day (the server's ledger aggregate). The same daily cap per
 * track as offline, applied day by day.
 */
export function standingFromCounts(rows: readonly { source: SourceKey; day: number; n: number }[]): Standing {
  const perDay = new Map<string, number>();
  const free: Standing = {};
  for (const r of rows) {
    const s = SOURCES[r.source];
    if (!s || !(r.n > 0)) continue;
    const pts = s.points * Math.floor(r.n);
    if (!s.capped) {
      free[s.track] = (free[s.track] ?? 0) + pts;
      continue;
    }
    const k = `${s.track}:${r.day}`;
    perDay.set(k, (perDay.get(k) ?? 0) + pts);
  }
  const out: Standing = { ...free };
  for (const [k, v] of perDay) {
    const track = k.slice(0, k.indexOf(':')) as ContactTrack;
    out[track] = (out[track] ?? 0) + Math.min(CONTACT_DAILY_CAP, v);
  }
  return out;
}

/** Rung per track for a standing. */
export function rungs(st: Standing): Record<ContactTrack, number> {
  const out = {} as Record<ContactTrack, number>;
  for (const t of CONTACT_TRACKS) out[t] = rungOf(st[t] ?? 0);
  return out;
}

const forMode = (p: Perk, mode: ContactMode): boolean => !p.mode || p.mode === mode;

/** The best value of `key` the rungs up to `rung` of `track` give in `mode` (0 = nothing). */
export function perkAt(track: ContactTrack, rung: number, key: PerkKey, mode: ContactMode): number {
  let v = 0;
  const def = TRACKS[track];
  for (let i = 0; i < Math.min(rung, def.rungs.length); i++) for (const p of def.rungs[i]!.perks) if (p.key === key && forMode(p, mode)) v = Math.max(v, p.value);
  return v;
}

/** Which track a perk belongs to (each key belongs to exactly one; contacts.test.ts asserts it). */
export function trackOfPerk(key: PerkKey): ContactTrack {
  for (const t of CONTACT_TRACKS) for (const r of TRACKS[t].rungs) if (r.perks.some((p) => p.key === key)) return t;
  return 'mags';
}

/** The value of `key` for this standing in `mode`. */
export function perk(st: Standing, key: PerkKey, mode: ContactMode): number {
  const t = trackOfPerk(key);
  return perkAt(t, rungOf(st[t] ?? 0), key, mode);
}

/** A price after a discount fraction (whole pounds, never below 0). */
export function discounted(base: number, frac: number): number {
  const f = Math.max(0, Math.min(1, frac));
  return Math.max(0, Math.round(base * (1 - f)));
}

/** The extra on a payout for a bonus fraction (whole pounds). */
export function bonusOn(base: number, frac: number): number {
  return Math.max(0, Math.round(Math.max(0, base) * Math.max(0, Math.min(MAX_BONUS, frac))));
}

/** The Anchor back room's price online for this standing: Wilf's gear discount, and his free holdall. */
export function shopPrice(defId: string, base: number, st: Standing): number {
  if (defId === 'holdall' && perk(st, 'holdall-free', 'online') > 0) return 0;
  return discounted(base, perk(st, 'gear', 'online'));
}

/** Pike's card for a list of priced lines: each line discounted on its own (client and server add up the same). */
export function linesPrice(lines: readonly { price: number }[], frac: number): number {
  let t = 0;
  for (const l of lines) t += discounted(l.price, frac);
  return t;
}

/** The perks a rung gives in `mode`. */
export function rungPerks(track: ContactTrack, rung: number, mode: ContactMode): Perk[] {
  const r = TRACKS[track].rungs[rung - 1];
  return r ? r.perks.filter((p) => forMode(p, mode)) : [];
}

/** The tracks shown in `mode`. */
export function tracksFor(mode: ContactMode): ContactTrack[] {
  return CONTACT_TRACKS.filter((t) => TRACKS[t].modes.includes(mode));
}

/** How a track's points are earned in `mode` ("a street job +8 · a matchbook found +4 (any day)"). */
export function earnText(track: ContactTrack, mode: ContactMode): string[] {
  return SOURCE_KEYS.filter((k) => SOURCES[k].track === track && SOURCES[k].modes.includes(mode)).map((k) => {
    const s = SOURCES[k];
    return `${s.label.charAt(0).toUpperCase()}${s.label.slice(1)}: +${s.points}${s.capped ? '' : ' (no daily limit)'}`;
  });
}

/** The highest title earned (the rung name), with whose it is; null before any. */
export function bestTitle(st: Standing, mode: ContactMode): { track: ContactTrack; title: string; rung: number } | null {
  let best: { track: ContactTrack; title: string; rung: number } | null = null;
  for (const t of tracksFor(mode)) {
    const r = rungOf(st[t] ?? 0);
    for (let i = r; i >= 1; i--) {
      const def = TRACKS[t].rungs[i - 1]!;
      if (def.title) {
        if (!best || i > best.rung) best = { track: t, title: def.name, rung: i };
        break;
      }
    }
  }
  return best;
}

/** Shape-only clean-up (a save, a server answer). */
export function sanitizeStanding(raw: unknown): Standing {
  const out: Standing = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (isTrack(k) && typeof v === 'number' && Number.isFinite(v) && v > 0) out[k] = Math.min(1_000_000, Math.floor(v));
  }
  return out;
}

export function sanitizeContacts(raw: unknown): ContactsState {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyContacts();
  const r = raw as Record<string, unknown>;
  const day = typeof r.day === 'number' && Number.isFinite(r.day) && r.day >= 0 ? Math.floor(r.day) : 0;
  const today = sanitizeStanding(r.today);
  for (const k of Object.keys(today) as ContactTrack[]) today[k] = Math.min(CONTACT_DAILY_CAP, today[k]!);
  return { pts: sanitizeStanding(r.pts), day, today };
}

// ----------------------------------------------------------------------------- the horizon (NEXT tracker)

/** The repeatable kind of work that climbs `track` fastest in `mode` (null: none). */
export function mainSource(track: ContactTrack, mode: ContactMode): SourceKey | null {
  let best: SourceKey | null = null;
  for (const k of SOURCE_KEYS) {
    const s = SOURCES[k];
    if (s.track !== track || !s.capped || !s.modes.includes(mode) || NOT_A_HORIZON.includes(k)) continue;
    if (!best || s.points > SOURCES[best].points) best = k;
  }
  return best;
}

/**
 * One plain line about the next rung with `track`, in units of its main work: "Halloran: 2 more export orders to
 * reach “A regular”. It opens: Export orders pay 5 % more." Null at the top or with no repeatable work in this mode.
 */
export function horizonLine(track: ContactTrack, st: Standing, mode: ContactMode): string | null {
  const p = progressOf(st[track] ?? 0);
  const src = mainSource(track, mode);
  if (p.next === null || !src) return null;
  const s = SOURCES[src];
  const units = Math.max(1, Math.ceil((p.next - p.points) / s.points));
  const def = TRACKS[track];
  const r = def.rungs[p.rung]!;
  const gets = rungPerks(track, p.rung + 1, mode)[0]?.text ?? (r.title ? `a title, “${r.name}”` : null);
  return `${def.short}: ${units} more ${units === 1 ? s.noun[0] : s.noun[1]} to reach “${r.name}”.${gets ? ` It opens: ${gets}.` : ''}`;
}

/** The track nearest its next rung (by units of work) among `tracks`; null when every one is at the top. */
export function nearestRung(tracks: readonly ContactTrack[], st: Standing, mode: ContactMode): ContactTrack | null {
  let best: ContactTrack | null = null;
  let bestUnits = Infinity;
  for (const t of tracks) {
    const p = progressOf(st[t] ?? 0);
    const src = mainSource(t, mode);
    if (p.next === null || !src) continue;
    const u = Math.ceil((p.next - p.points) / SOURCES[src].points);
    if (u < bestUnits) {
      bestUnits = u;
      best = t;
    }
  }
  return best;
}
